import { Box, Picture, Shimmer, Typography } from "@jewlr/storybook/core"
import { useQuery } from "@tanstack/react-query"
import PropTypes from "prop-types"
import { useCallback } from "react"
import { Helmet } from "react-helmet-async"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import styled from "styled-components"

import api from "apis"
import GenericNotFound from "app/generic-not-found"

import ArticleSection from "./article-section"
import Breadcrumb from "./breadcrumb"
import ShareArticle from "./share-article"

const Heading = styled(Typography).attrs({
  as: "h1",
})`
  color: black;
  font-size: 30px;
  font-weight: 700;
  line-height: 30px;
  margin: 0;
`

const Container = styled(Box)`
  margin: auto;
  width: 100%;
  ${(props) => props.theme.mediaQueries.desktop`
    width: 900px;
  `}
`

const Article = ({ articleData, staticContext }) => {
  const { article_path } = useParams()
  const queryFn = useCallback(
    () => api.get(`/blog/${article_path}.json`),
    [article_path]
  )

  const ssrData = typeof window === "undefined" ? articleData : undefined
  const ssrNotFoundPath = useSelector(
    (state) => state.collection.ssr_not_found_path
  )
  const disabled =
    `${sl.basename || ""}/blog/${article_path}` === ssrNotFoundPath

  const { data, isError, isLoading } = useQuery({
    enabled: !disabled,
    keepPreviousData: true,
    queryFn,
    queryKey: ["blog", article_path],
    ...(ssrData && {
      initialData: ssrData,
    }),
  })

  if (isError || disabled) {
    return <GenericNotFound staticContext={staticContext} />
  }

  const { article } = data || {}

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
        {(article?.seo_title || article?.title) && (
          <>
            <title>{article.seo_title || article.title}</title>
            <meta
              content={article.seo_title || article.title}
              property="og:title"
            />
          </>
        )}
        {article?.seo_description && (
          <meta content={article.seo_description} name="description" />
        )}
        {article?.seo_description && (
          <meta content={article.seo_description} property="og:description" />
        )}
        <meta content="article" property="og:type" />
        {article?.url_path && (
          <meta
            content={`https://${sl.config.site_url}/blog/${article.url_path}`}
            property="og:url"
          />
        )}
        {article?.cover_image?.fallback && (
          <meta
            content={article.cover_image.fallback}
            name="og_image"
            property="og:image"
          />
        )}
        {article?.cover_image?.srcSet?.length > 0 &&
          article.cover_image.srcSet.map((srcset, i) => (
            <link
              key={`article-cover-image-${article.cover_image.id || i}-${
                srcset.media
              }`}
              rel="preload"
              fetchPriority="high"
              as="image"
              imageSrcSet={srcset.srcSet}
              imageSizes={srcset.sizes}
              media={srcset.media}
            />
          ))}
        {article?.updatedAt && (
          <meta content={article.updatedAt} property="article:modified_time" />
        )}
        {article && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `https://${sl.config.site_url}/blog/${article.url_path}`,
              },
              headline: article.title,
              ...(article.cover_image?.fallback && {
                image: article.cover_image.fallback,
              }),
              ...(article.updatedAt && {
                dateModified: article.updatedAt,
              }),
              author: {
                "@type": "Organization",
                name: sl.config.site_title,
                url: `https://${sl.config.site_url}`,
              },
              publisher: {
                "@type": "Organization",
                name: sl.config.site_title,
                url: `https://${sl.config.site_url}/blog/${article.url_path}`,
                logo: {
                  "@type": "ImageObject",
                  url: "https://assets.jewlr.com/images/logos/jewlr-logo.svg",
                },
              },
            })}
          </script>
        )}
        {/* eslint-enable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <Breadcrumb article={article?.title} isLoading={isLoading} />
      {isLoading ? (
        <>
          <Shimmer height="54px" mb={{ _: 3, tablet: 4 }} width="100%" />
          <Shimmer
            mb={{ _: 2, tablet: 6 }}
            pb={{ _: "65.89%", tablet: "40.32%" }}
            width="100%"
          />
          <Shimmer
            height="80vh"
            mx="auto"
            width={{ _: "100%", desktop: "900px" }}
          />
        </>
      ) : (
        <>
          <div className="tablet:mb-32 mb-24">
            <Heading>{article.title}</Heading>
            {article.formattedUpdatedAt && (
              <p className="body-sm leading-16 text-grey-65 mt-8">
                Last Updated: {article.formattedUpdatedAt}
              </p>
            )}
          </div>
          {article.cover_image && (
            <Box mb={{ _: 2, tablet: 6 }}>
              <Picture
                alt={article.cover_image.alt}
                fallback={article.cover_image.fallback}
                srcSet={article.cover_image.srcSet}
              />
            </Box>
          )}
          <Container>
            {article.sections &&
              article.sections.map((section, i) => (
                <ArticleSection key={i} section={section} />
              ))}
          </Container>
          <ShareArticle article={article} />
        </>
      )}
    </>
  )
}

Article.propTypes = {
  articleData: PropTypes.object,
  staticContext: PropTypes.object,
}

export default Article
